'use client';

import React from 'react';
import dynamic from 'next/dynamic';

import { useForm } from 'react-hook-form';

import { Link } from '~/components/common/link';
import { SelectedCity } from '~/components/forms/hospital-sponsorship-form/city-search';
import { HospitalListing } from '~/components/forms/hospital-sponsorship-form/hospital-listing';
import { USStatesSprite } from '~/components/forms/hospital-sponsorship-form/united-states';
import { FancyRadioButton } from '~/components/ui/fancy-radio-button';
import { cn } from '~/lib/utils';
import { AlgoliaHospitalRecord } from '~/types/algolia';

import s from '../forms/hospital-sponsorship-form/hospital-sponsorship.module.css';
import { replaceAsteriskedTextWithFancyUnderline } from './fancy-underline';

const CitySearch = dynamic(
  () => import('~/components/forms/hospital-sponsorship-form/city-search').then((mod) => ({ default: mod.CitySearch })),
  {
    ssr: false,
    loading: () => <div className='bg-asphalt-25/75 mx-auto h-12 max-w-md animate-pulse rounded-lg' />,
  },
);

interface SearchHospitalsProps {
  headline?: string;
  subhead?: string;
}

export function SearchHospitals({ headline, subhead }: SearchHospitalsProps) {
  const [searchMethod, setSearchMethod] = React.useState<'city' | 'hospital'>('city');
  const [hospital, setHospital] = React.useState<AlgoliaHospitalRecord | null>(null);
  const [suggestion, setSuggestion] = React.useState<SelectedCity | null>(null);

  const { setValue } = useForm<{ searched_by: string }>({
    defaultValues: {
      searched_by: '',
    },
  });

  React.useEffect(() => {
    const locationInput = document.querySelector<HTMLElement>('.ap-input');
    const hospitalNameInput = document.querySelector<HTMLElement>('.ais-SearchBox-input');

    if (searchMethod === 'city' && locationInput !== null) {
      locationInput.focus();
    }

    if (searchMethod === 'hospital' && hospitalNameInput !== null) {
      hospitalNameInput.focus();
    }
  }, [searchMethod]);

  return (
    <section className={cn('mx-auto w-full max-w-5xl px-6 sm:px-12', s.search)}>
      <div className='mx-auto mb-6 flex max-w-2xl flex-col items-center lg:mb-8'>
        <h2 className='heading-lg max-w-lg text-center'>
          {headline ? replaceAsteriskedTextWithFancyUnderline(headline) : 'Search our hospitals'}
        </h2>
        <p className='text-muted-foreground body-lg mt-6 text-center lg:mt-8'>
          {subhead ? (
            subhead
          ) : (
            <>
              Find hospitals we serve and learn how to{' '}
              <Link className='underline' href='/fund-a-program'>
                fund a program
              </Link>
              .
            </>
          )}
        </p>
      </div>
      <div className='mx-auto mb-4 grid max-w-md grid-cols-2 gap-2 lg:gap-4'>
        <FancyRadioButton
          isActive={searchMethod === 'city'}
          onClick={() => {
            setValue('searched_by', 'Location');
            setSearchMethod('city');
          }}
          size='small'
          type='button'
        >
          By City/Location
        </FancyRadioButton>
        <FancyRadioButton
          isActive={searchMethod === 'hospital'}
          onClick={() => {
            setValue('searched_by', 'Hospital name');
            setSearchMethod('hospital');
          }}
          size='small'
          type='button'
        >
          By Hospital Name
        </FancyRadioButton>
      </div>
      {searchMethod === 'city' && (
        <React.Suspense fallback={null}>
          <CitySearch
            onClear={() => {
              setSuggestion(null);
            }}
            onSelectCity={(city) => setSuggestion(city)}
          />
        </React.Suspense>
      )}
      <HospitalListing
        onSelectHospital={(hit) => {
          setHospital(hit);
        }}
        searchMethod={searchMethod}
        selectedCity={suggestion}
        selectedHospital={hospital}
        variant='search'
      />
      <USStatesSprite />
    </section>
  );
}
